export type WatchBestPositionParams = {
    maxWatchMs: number;
    stopWhenAccuracyLTE?: number;
    options: PositionOptions;
};

export function getCurrentPosition(options?: PositionOptions): Promise<GeolocationPosition> {
    return new Promise((resolve, reject) => {
        if (typeof navigator === "undefined" || !("geolocation" in navigator)) {
            reject(new Error("Geolocation is not supported by this environment."));
            return;
        }
        navigator.geolocation.getCurrentPosition(resolve, reject, options);
    });
}

/**
 * Watches position for up to `maxWatchMs`, returning the best (lowest accuracy) fix observed.
 * Rejects only on PERMISSION_DENIED; other errors resolve with the best known fix (or null).
 */
export function watchBestPosition({
    maxWatchMs,
    stopWhenAccuracyLTE,
    options,
}: WatchBestPositionParams): Promise<GeolocationPosition | null> {
    return new Promise((resolve, reject) => {
        if (typeof navigator === "undefined" || !("geolocation" in navigator)) {
            resolve(null);
            return;
        }

        let watchId: number | null = null;
        let finished = false;
        let best: GeolocationPosition | null = null;

        const cleanup = () => {
            if (watchId != null) {
                navigator.geolocation.clearWatch(watchId);
                watchId = null;
            }
        };

        const finish = (pos: GeolocationPosition | null) => {
            if (finished) return;
            finished = true;
            cleanup();
            resolve(pos);
        };

        const timer = window.setTimeout(() => finish(best), maxWatchMs);

        watchId = navigator.geolocation.watchPosition(
            (pos) => {
                const acc = pos.coords.accuracy;
                const bestAcc = best?.coords?.accuracy;

                if (
                    best == null ||
                    (typeof acc === "number" &&
                        Number.isFinite(acc) &&
                        (typeof bestAcc !== "number" || !Number.isFinite(bestAcc) || acc < bestAcc))
                ) {
                    best = pos;
                }

                if (
                    stopWhenAccuracyLTE != null &&
                    typeof acc === "number" &&
                    Number.isFinite(acc) &&
                    acc <= stopWhenAccuracyLTE
                ) {
                    window.clearTimeout(timer);
                    finish(pos);
                }
            },
            (error) => {
                window.clearTimeout(timer);
                cleanup();

                if (error?.code === 1) {
                    reject(error);
                    return;
                }

                resolve(best);
            },
            {
                ...options,
                timeout: maxWatchMs,
            }
        );
    });
}

